import * as React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  FlaskConicalIcon,
  BarChartIcon,
  DatabaseIcon,
  FileTextIcon,
  FolderIcon,
  HelpCircleIcon,
  LayoutDashboardIcon,
  SearchIcon,
  SettingsIcon,
  UsersIcon,
  PackageIcon,
  TrendingUpIcon,
  LeafIcon,
  Beaker,
  ClipboardListIcon,
  CheckCircleIcon,
  InfoIcon
} from "lucide-react"

// SpyGlass Brand Icon - consistent with main app
const SpyglassBrandIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="12" height="12" rx="2" stroke="#4F46E5" strokeWidth="2" fill="none"/> {/* Indigo-600 (Top-Left Outlined Square) */}
    {/* Sky-500 (Top-Right Diamond - Rotated Square) */}
    <rect x="18" y="4" width="12" height="12" rx="2" fill="#0EA5E9" transform="rotate(45 24 10)"/> 
    {/* Blue-500 (Bottom-Left Triangle - Pointing Up) */}
    <path d="M4 30 L16 30 L10 18 Z" fill="#3B82F6"/> 
    <circle cx="24" cy="24" r="6" fill="#A5B4FC"/>                        {/* Indigo-300 (Bottom-Right Filled Circle) */}
  </svg>
)

import { NavDocuments } from "@/components/nav-documents"
import { NavMain } from "@/components/nav-main"
import { NavSecondary } from "@/components/nav-secondary"
import { NavUser } from "@/components/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"

const data = {
  user: {
    name: "SpyGlass User",
    email: "analyst@spyglass.com",
    avatar: "/avatars/spyglass-user.jpg",
  },
  navMain: [
    {
      title: "Dashboard",
      url: "/reports",
      icon: LayoutDashboardIcon,
    },
    {
      title: "Overview",
      url: "#overview",
      icon: InfoIcon,
    },
    {
      title: "Product Analysis",
      url: "#",
      icon: FlaskConicalIcon,
    },
    {
      title: "Market Intelligence",
      url: "#",
      icon: TrendingUpIcon,
    },
    {
      title: "Supply Chain",
      url: "#",
      icon: PackageIcon,
    },
    {
      title: "Sustainability",
      url: "#",
      icon: LeafIcon,
    },
    {
      title: "Product Recommendations",
      url: "#",
      icon: CheckCircleIcon,
    },
  ],
  navAnalysis: [
    {
      title: "Formulations",
      icon: Beaker,
      isActive: true,
      url: "#",
      items: [
        {
          title: "Active Projects",
          url: "#",
        },
        {
          title: "Completed",
          url: "#",
        },
        {
          title: "Templates",
          url: "#",
        },
      ],
    },
    {
      title: "Analysis Reports",
      icon: FileTextIcon,
      url: "/reports",
      items: [
        {
          title: "Recent Reports",
          url: "/reports",
        },
        {
          title: "Scheduled",
          url: "#",
        },
        {
          title: "Export Center",
          url: "#",
        },
      ],
    },
    {
      title: "Data Sources",
      icon: DatabaseIcon,
      url: "#",
      items: [
        {
          title: "Market Data",
          url: "#",
        },
        {
          title: "Ingredient Database",
          url: "#",
        },
        {
          title: "Supply Chain",
          url: "#",
        },
      ],
    },
  ],
  navSecondary: [
    {
      title: "Settings",
      url: "#",
      icon: SettingsIcon,
    },
    {
      title: "Help & Support",
      url: "#",
      icon: HelpCircleIcon,
    },
    {
      title: "Search",
      url: "#",
      icon: SearchIcon,
    },
  ],
  documents: [
    {
      name: "Ingredient Library",
      url: "#",
      icon: DatabaseIcon,
    },
    {
      name: "Analysis Reports",
      url: "/reports",
      icon: ClipboardListIcon,
    },
    {
      name: "Documentation",
      url: "#",
      icon: FileTextIcon,
    },
  ],
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation()
  const navigate = useNavigate()
  
  // Function to handle section navigation
  const handleSectionNavigation = (sectionId: string) => {
    // If we're on the dashboard page, handle navigation
    if (location.pathname.includes('/dashboard/') || location.pathname.includes('/results/')) {
      // Send message to parent component to switch to detailed view and scroll
      const event = new CustomEvent('navigateToSection', { 
        detail: { sectionId } 
      });
      window.dispatchEvent(event);
    } else {
      // If we're not on a dashboard page, navigate to reports first
      navigate('/reports');
    }
  };

  // Updated navMain with section navigation
  const navMainWithNavigation = [
    {
      title: "Dashboard",
      url: "/reports",
      icon: LayoutDashboardIcon,
    },
    {
      title: "Overview",
      url: "#overview",
      icon: InfoIcon,
      onClick: () => handleSectionNavigation('overview')
    },
    {
      title: "Product Analysis",
      url: "#",
      icon: FlaskConicalIcon,
      onClick: () => handleSectionNavigation('product-analysis')
    },
    {
      title: "Market Intelligence",
      url: "#",
      icon: TrendingUpIcon,
      onClick: () => handleSectionNavigation('market-intelligence')
    },
    {
      title: "Supply Chain",
      url: "#",
      icon: PackageIcon,
      onClick: () => handleSectionNavigation('supply-chain')
    },
    {
      title: "Sustainability",
      url: "#",
      icon: LeafIcon,
      onClick: () => handleSectionNavigation('sustainability')
    },
    {
      title: "Product Recommendations",
      url: "#",
      icon: CheckCircleIcon,
      onClick: () => handleSectionNavigation('product-recommendations')
    },
  ];
  
  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="data-[slot=sidebar-menu-button]:!p-1.5"
            >
              <a href="/">
                <SpyglassBrandIcon />
                <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  SpyGlass <span className="font-light text-sky-600">AI</span>
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navMainWithNavigation} currentPath={location.pathname} />
        <NavDocuments items={data.documents} currentPath={location.pathname} />
        <NavSecondary items={data.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
    </Sidebar>
  )
}
